import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreDocument, DocumentReference, QueryFn } from '@angular/fire/firestore';
import { Observable, of, throwError, zip } from 'rxjs';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { GruEje } from '@common/interfaces/grueje.interface';
import { Brand } from '@common/interfaces/brand.interface';
import { firestoreMaxClauses } from '@common/const/firebase-max-clauses.const';
import { BrandService } from '../brand/brand.service';
import { TipoEjeManual } from '@common/models/tipo-eje-manual.model';

// Helper to create a placeholder for empty 'in' arrays to avoid Firestore errors.
// Firestore queries with 'in' require a non-empty array.
// Using a value that is highly unlikely to exist in the queried field.
const nonMatchingNumericPlaceholder = [-1]; // For numeric fields
const nonMatchingStringPlaceholder = ['__NON_MATCHING_PLACEHOLDER__']; // For string fields

@Injectable({
  providedIn: 'root'
})
export class AxisService {
  public static collectionName = 'GRUEJE';
  public static readonly tipoEjeCollectionName = 'TIPO_EJE_MANUAL';

  constructor(
    private af: AngularFirestore,
    private brandService: BrandService
  ) { }

  public getAxis({ brand, axis, subaxis, line }: { brand: number, axis: number, subaxis?: string, line?: string }): Observable<GruEje> {
    if (line && !subaxis) {
      // For RxJS 6, throwError('message') is valid. For RxJS 7+, use throwError(() => new Error('message'))
      return throwError('If you specify "line" you need to specify "subaxis" too.');
    }

    return this.getAxisDoc({ brand, axis, subaxis, line }).valueChanges().pipe(
      switchMap(a => this.axisMapper(a)) // axisMapper should gracefully handle undefined/null 'a'
    );
  }

  public getAxes(brands: Brand[]): Observable<GruEje[]> {
    const validBrands = brands || [];
    if (validBrands.length === 0) {
      return of([]);
    }

    return this.genericGetAxes(validBrands, chunkIndex => {
      const offset = chunkIndex * firestoreMaxClauses;
      console.log("MARCAS", validBrands)
      const brandIDs = validBrands
        .slice(offset, offset + firestoreMaxClauses)
        .map(b => b.ID_MARCA)
        .filter(id => id != null); // Ensure no null IDs

      return ref => ref
        .where('ID_MARCA', 'in', brandIDs.length > 0 ? brandIDs : nonMatchingNumericPlaceholder)
        .where('SUBEJE_CLASE', '==', '')
        .where('LINEA', '==', '');
    });
  }

  public getSubaxes(axes: GruEje[]): Observable<GruEje[]> {
    const validAxes = axes || [];
    if (validAxes.length === 0) {
      return of([]);
    }

    return this.genericGetAxes(validAxes, chunkIndex => {
      const offset = chunkIndex * firestoreMaxClauses;
      const axisCodes = validAxes
        .slice(offset, offset + firestoreMaxClauses)
        .map(axis => axis.CODIGO_EJE)
        .filter(code => code != null && !isNaN(Number(code))); // Ensure no null/undefined and is a number

      return ref => ref
        .where('CODIGO_EJE', 'in', axisCodes.length > 0 ? axisCodes : nonMatchingNumericPlaceholder)
        .where('SUBEJE_CLASE', '!=', '')
        .where('LINEA', '==', '');
    });
  }

  public getLines(subaxes: GruEje[], isGC: boolean): Observable<GruEje[]> {
    const validSubaxes = subaxes || [];
    if (validSubaxes.length === 0) {
      return of([]);
    }
    
    return this.genericGetAxes(validSubaxes, chunkIndex => {
      const offset = chunkIndex * firestoreMaxClauses;
      const subaxisClasses = validSubaxes
        .slice(offset, offset + firestoreMaxClauses)
        .map(subaxis => subaxis.SUBEJE_CLASE)
        .filter(clase => clase != null); // Ensure no null classes (empty strings are valid)

      return ref => {
        let query = ref.where('SUBEJE_CLASE', 'in', subaxisClasses.length > 0 ? subaxisClasses : nonMatchingStringPlaceholder);
        if (!isGC) {
          query = query.where('LINEA', '!=', '');
        }
        return query;
      };
    });
  }
  
  public getAxisRef(axisConfig: { brand: number, axis: number, subaxis?: string, line?: string }): DocumentReference<GruEje> {
    return this.getAxisDoc(axisConfig).ref;
  }

  private getAxisDoc({
    brand,
    axis,
    subaxis,
    line
  }: {
    brand: number,
    axis: number,
    subaxis?: string,
    line?: string
  }): AngularFirestoreDocument<GruEje> {
    const subaxisSegment = subaxis ? `-${subaxis}` : '';
    const lineSegment = line ? `-${line}` : '';
    const id = `${brand}-${axis}${subaxisSegment}${lineSegment}`;
    return this.af.doc<GruEje>(`${AxisService.collectionName}/${id}`);
  }

  private genericGetAxes(elements: (Brand | GruEje)[], queryBuilderFn: (chunkIndex: number) => QueryFn<GruEje>): Observable<GruEje[]> {
    // elements is already ensured to be non-empty by callers, or this method won't be called.
    // However, a defensive check here for elements.length === 0 could return of([]) immediately.
    if (!elements || elements.length === 0) {
        return of([]);
    }

    const numChunks = Math.ceil(elements.length / firestoreMaxClauses);
    const chunkObservables: Observable<GruEje[]>[] = [];

    for (let i = 0; i < numChunks; i++) {
        // queryBuilderFn now correctly uses the chunkIndex to determine the slice of 'elements'
        // and to form the query for that specific chunk.
        const queryFnForChunk = queryBuilderFn(i);
        
        // The queryFnForChunk itself handles empty 'in' arrays by using placeholders.
        // So, we can directly create the collection observable.
        chunkObservables.push(
            this.af.collection<GruEje>(
                AxisService.collectionName,
                queryFnForChunk
            ).valueChanges()
        );
    }
    
    return zip(...chunkObservables).pipe(
      map(arrayOfArrays => arrayOfArrays.flat()),
      defaultIfEmpty([] as GruEje[]), // In case all chunks return empty or elements was empty
      switchMap(fetchedItems => {
        if (fetchedItems.length === 0) {
          return of([] as GruEje[]);
        }
        // Map each fetched item using axisMapper
        const mappedItemsObservables = fetchedItems.map(item => this.axisMapper(item));
        return zip(...mappedItemsObservables).pipe(defaultIfEmpty([] as GruEje[]));
      }),
      map(mappedResult => {
        // Filter based on original elements' ID_MARCA and specific CODIGO_EJE values
        // Ensure ID_MARCA from elements is valid before using in includes
        const validBrandIdsFromElements = elements
            .map(e => e.ID_MARCA)
            .filter(id => id != null);

        return mappedResult.filter(r => 
            r && // Ensure r is not null/undefined after mapping
            r.MARCA && // Ensure MARCA object exists
            validBrandIdsFromElements.includes(r.MARCA.ID_MARCA) && 
            ![0, 99].includes(r.CODIGO_EJE)
        );
      }),
      map(finalResult => finalResult.sort((a, b) => a.CODIGO_EJE > b.CODIGO_EJE ? 1 : (a.CODIGO_EJE < b.CODIGO_EJE ? -1 : 0)))
    );
  }

  public axisMapper(axis: GruEje | undefined | null): Observable<GruEje | undefined | null> {
    if (!axis || axis.ID_MARCA == null) { // also check if ID_MARCA is null/undefined
      return of(axis);
    }

    return this.brandService.getBrandById(axis.ID_MARCA).pipe(
      map(brand => ({
        ...axis,
        MARCA: brand // brand could be null if not found by BrandService
      }))
    );
  }

  getEjeManual(codigoEjeManual: number): Observable<TipoEjeManual | undefined> {
    return this.af.doc<TipoEjeManual>(`${AxisService.tipoEjeCollectionName}/${codigoEjeManual}`)
      .get().pipe(map(doc => doc.data() as TipoEjeManual | undefined)); // Ensure type cast for data()
  }
}